import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Building2, Briefcase, Mail, Phone, MapPin, User, ArrowLeft, ArrowRight,
  CheckCircle2, Loader2, Shield, FileText, Globe, Users, Hash,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOTAL_STEPS = 4;

export default function CompanyRegistration() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    company_name: '', registration_number: '', vat_number: '',
    company_email: '', company_phone: '', website: '',
    physical_address: '', province: '', country: 'South Africa',
    primary_contact_name: '', primary_contact_position: '',
    primary_contact_phone: '', primary_contact_email: '',
    buildings_managed: 0, units_managed: 0, areas_of_operation: '',
    accept_terms: false, accept_authorised: false,
  });

  // Pre-fill from agent-request "company not listed" flow
  useEffect(() => {
    const prefillEmail = params.get('prefill_email');
    const prefillCompany = params.get('prefill_company');
    if (prefillEmail || prefillCompany) {
      setForm((f) => ({
        ...f,
        ...(prefillEmail ? { primary_contact_email: prefillEmail, company_email: f.company_email || prefillEmail } : {}),
        ...(prefillCompany ? { company_name: prefillCompany } : {}),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setNum = (k) => (e) => setForm({ ...form, [k]: parseInt(e.target.value || '0', 10) });
  const setBool = (k) => (v) => setForm({ ...form, [k]: !!v });

  const validateStep = () => {
    if (step === 1) {
      if (!form.company_name || !form.company_email || !form.company_phone || !form.physical_address || !form.country) {
        toast.error('Please complete all required company fields'); return false;
      }
    }
    if (step === 2) {
      if (!form.primary_contact_name || !form.primary_contact_position || !form.primary_contact_phone || !form.primary_contact_email) {
        toast.error('Please complete all required contact fields'); return false;
      }
    }
    if (step === 4) {
      if (!form.accept_terms || !form.accept_authorised) {
        toast.error('Please accept the Terms & confirm authorisation'); return false;
      }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(TOTAL_STEPS, s + 1)); };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/company-onboarding/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) { setSuccess(true); toast.success('Registration submitted!'); }
      else toast.error(data.detail || 'Submission failed');
    } catch (e) {
      toast.error('Network error — please retry');
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-sky-50 to-emerald-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg bg-white border-slate-200 shadow-lg" data-testid="onboarding-success-card">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Registration Submitted!</h2>
            <p className="text-slate-600 mb-4">
              Thank you for registering <strong>{form.company_name}</strong> with BodyCorp365.
            </p>
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 text-left mb-6">
              <p className="text-sm text-slate-700 font-medium mb-2">What happens next:</p>
              <ol className="text-sm text-slate-600 space-y-1 list-decimal pl-5">
                <li>You will receive a confirmation email with your Letter of Intent attached.</li>
                <li>Our team reviews your application within 2 business days.</li>
                <li>Once approved you will receive an activation email with an OTP & link.</li>
                <li>Activate your account and start managing buildings.</li>
              </ol>
            </div>
            <p className="text-sm text-slate-500 mb-6">
              Confirmation sent to <span className="text-indigo-600 font-medium">{form.primary_contact_email}</span>
            </p>
            <div className="flex flex-col gap-3">
              <Button onClick={() => navigate('/login')} className="bg-indigo-600 hover:bg-indigo-700 shadow-md" data-testid="goto-login-btn">Go to Login</Button>
              <Button variant="outline" onClick={() => navigate('/')} className="border-slate-300 text-slate-700 hover:bg-slate-50">Back to Home</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progress = (step / TOTAL_STEPS) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-sky-50 to-emerald-50">
      <header className="p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-700 hover:text-indigo-600">
            <ArrowLeft className="w-5 h-5" /><span>Back to Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-slate-800">BodyCorp365</span>
          </div>
        </div>
      </header>

      <main className="flex justify-center p-4 pb-12">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Register Your Management Company</h1>
            <p className="text-slate-600">Professional onboarding for property management companies</p>
          </div>

          {/* Stepper */}
          <div className="bg-white rounded-xl p-4 border border-slate-200 mb-4">
            <div className="flex items-center justify-between mb-2 text-xs text-slate-500">
              <span>Step {step} of {TOTAL_STEPS}</span>
              <span data-testid="step-label">
                {step === 1 && 'Company Information'}
                {step === 2 && 'Primary Contact'}
                {step === 3 && 'Portfolio'}
                {step === 4 && 'Terms & LOI'}
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-600 transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <Card className="bg-white border-slate-200 shadow-lg">
            <CardHeader>
              {step === 1 && <><CardTitle className="text-slate-800">Company Information</CardTitle><CardDescription>Legal & contact details of the management company</CardDescription></>}
              {step === 2 && <><CardTitle className="text-slate-800">Primary Contact (Super Agent)</CardTitle><CardDescription>The person who will administer this account</CardDescription></>}
              {step === 3 && <><CardTitle className="text-slate-800">Portfolio</CardTitle><CardDescription>Your current management footprint</CardDescription></>}
              {step === 4 && <><CardTitle className="text-slate-800">Letter of Intent & Terms</CardTitle><CardDescription>Review and accept to submit</CardDescription></>}
            </CardHeader>
            <CardContent className="space-y-4">
              {step === 1 && (
                <>
                  <Field icon={Briefcase} label="Company Name *">
                    <Input value={form.company_name} onChange={set('company_name')} placeholder="PropManage (Pty) Ltd" data-testid="company-name-input" />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field icon={Hash} label="Registration #">
                      <Input value={form.registration_number} onChange={set('registration_number')} placeholder="2021/123456/07" data-testid="reg-number-input" />
                    </Field>
                    <Field icon={Hash} label="VAT #">
                      <Input value={form.vat_number} onChange={set('vat_number')} placeholder="4123456789" data-testid="vat-number-input" />
                    </Field>
                  </div>
                  <Field icon={Mail} label="Company Email *">
                    <Input type="email" value={form.company_email} onChange={set('company_email')} placeholder="info@propmanage.co.za" data-testid="company-email-input" />
                  </Field>
                  <Field icon={Phone} label="Company Phone *">
                    <Input value={form.company_phone} onChange={set('company_phone')} placeholder="+27 11 123 4567" data-testid="company-phone-input" />
                  </Field>
                  <Field icon={Globe} label="Website">
                    <Input value={form.website} onChange={set('website')} placeholder="https://propmanage.co.za" data-testid="website-input" />
                  </Field>
                  <Field icon={MapPin} label="Physical Address *">
                    <Input value={form.physical_address} onChange={set('physical_address')} placeholder="123 Business Park, Sandton" data-testid="address-input" />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field icon={MapPin} label="Province">
                      <Input value={form.province} onChange={set('province')} placeholder="Gauteng" data-testid="province-input" />
                    </Field>
                    <Field icon={Globe} label="Country *">
                      <Input value={form.country} onChange={set('country')} placeholder="South Africa" data-testid="country-input" />
                    </Field>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <Field icon={User} label="Full Name *">
                    <Input value={form.primary_contact_name} onChange={set('primary_contact_name')} placeholder="Jane Doe" data-testid="contact-name-input" />
                  </Field>
                  <Field icon={Briefcase} label="Position *">
                    <Input value={form.primary_contact_position} onChange={set('primary_contact_position')} placeholder="Managing Director" data-testid="contact-position-input" />
                  </Field>
                  <Field icon={Mail} label="Email *">
                    <Input type="email" value={form.primary_contact_email} onChange={set('primary_contact_email')} placeholder="jane@propmanage.co.za" data-testid="contact-email-input" />
                  </Field>
                  <Field icon={Phone} label="Phone *">
                    <Input value={form.primary_contact_phone} onChange={set('primary_contact_phone')} placeholder="+27 82 123 4567" data-testid="contact-phone-input" />
                  </Field>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    The primary contact becomes the <strong>Super Agent</strong> of this company on activation, with full admin rights.
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Field icon={Building2} label="Buildings Managed">
                      <Input type="number" min="0" value={form.buildings_managed} onChange={setNum('buildings_managed')} data-testid="buildings-input" />
                    </Field>
                    <Field icon={Users} label="Units Managed">
                      <Input type="number" min="0" value={form.units_managed} onChange={setNum('units_managed')} data-testid="units-input" />
                    </Field>
                  </div>
                  <Field icon={MapPin} label="Areas of Operation">
                    <Textarea value={form.areas_of_operation} onChange={set('areas_of_operation')} placeholder="Cape Town, Johannesburg, Durban" data-testid="areas-input" rows={3} />
                  </Field>
                </>
              )}

              {step === 4 && (
                <>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 max-h-64 overflow-y-auto text-sm text-slate-700 leading-relaxed">
                    <p className="font-semibold mb-2 flex items-center gap-2"><FileText className="w-4 h-4" /> Letter of Intent — Summary</p>
                    <p className="mb-2">By submitting this registration, you confirm that <strong>{form.company_name || '[your company]'}</strong> wishes to evaluate and potentially partner with BodyCorp365 for property management software services.</p>
                    <ul className="list-disc pl-5 space-y-1 mb-2">
                      <li>Centralised communication between residents, trustees and managing agents.</li>
                      <li>Digital management of maintenance, levies and documents.</li>
                      <li>Improved transparency, governance and compliance.</li>
                      <li>Reduced administrative workload through automation.</li>
                    </ul>
                    <p className="text-xs text-slate-500">A formal LOI document (PDF & DOCX) is generated and emailed to you and BodyCorp365 on submission. This is an expression of interest and is not a binding commercial contract.</p>
                  </div>

                  <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                    <Checkbox checked={form.accept_terms} onCheckedChange={setBool('accept_terms')} data-testid="accept-terms-checkbox" />
                    <span className="text-sm text-slate-700">I accept the BodyCorp365 <Link to="/terms" className="text-indigo-600 underline">Terms &amp; Conditions</Link> and <Link to="/privacy" className="text-indigo-600 underline">Privacy Policy</Link>.</span>
                  </label>
                  <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                    <Checkbox checked={form.accept_authorised} onCheckedChange={setBool('accept_authorised')} data-testid="accept-authorised-checkbox" />
                    <span className="text-sm text-slate-700">I confirm that I am authorised to submit this Letter of Intent on behalf of the company.</span>
                  </label>
                  <div className="text-xs text-slate-500 flex items-start gap-2"><Shield className="w-4 h-4 mt-0.5" /> Your IP address and timestamp will be captured as proof of acceptance.</div>
                </>
              )}

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={prev} disabled={step === 1} className="border-slate-300" data-testid="prev-step-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
                {step < TOTAL_STEPS ? (
                  <Button onClick={next} className="bg-indigo-600 hover:bg-indigo-700" data-testid="next-step-btn">
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button onClick={submit} disabled={loading || !form.accept_terms || !form.accept_authorised} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-application-btn">
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting…</> : <>Submit & Generate LOI</>}
                  </Button>
                )}
              </div>

              <p className="text-center text-slate-600 text-sm pt-2">
                Already activated?{' '}
                <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-medium">Log in here</Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-slate-700">{label}</Label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 pointer-events-none" />
        <div className="[&_input]:pl-10 [&_textarea]:pl-10">{children}</div>
      </div>
    </div>
  );
}
